/* eslint-disable @ngrx/prefer-effect-callback-in-block-statement */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';

import { RoleFacade } from '@cdba/core/auth/role-facade/role.facade';
import { ProductDetailService } from '@cdba/detail/service/detail.service';
import { CalculationsResponse } from '@cdba/shared/models/responses';

import {
  loadCalculationHistory,
  loadCalculationHistoryFailure,
  loadCalculationHistorySuccess,
  loadCalculations,
} from '../../actions';
import { loadComparisonFeatureData } from '../../actions/root/compare-root.actions';
import { getSelectedReferenceTypeIdentifiers } from '../../selectors';

@Injectable()
export class CalculationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly productDetailService = inject(ProductDetailService);
  private readonly store = inject(Store);
  private readonly roleFacade = inject(RoleFacade);

  loadCalculations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCalculations),
      concatLatestFrom(() =>
        this.store.select(getSelectedReferenceTypeIdentifiers)
      ),
      map(([_action, identifiers]) => identifiers),
      mergeMap((identifiers) =>
        identifiers.map((identifier, index) =>
          loadCalculationHistory({
            index,
            materialNumber: identifier.materialNumber,
            plant: identifier.plant,
          })
        )
      )
    )
  );

  loadCalculationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCalculationHistory),
      concatLatestFrom(() => this.roleFacade.hasAnyPricingRole$),
      mergeMap(([action, hasPricingRole]) =>
        hasPricingRole
          ? this.productDetailService
              .getCalculations(action.materialNumber, action.plant)
              .pipe(
                map((response: CalculationsResponse) =>
                  loadCalculationHistorySuccess({
                    items: response.items,
                    numberOfUnauthorizedCalculations:
                      response.numberOfUnauthorizedCalculations,
                    missingCostRoles: response.missingCostRoles,
                    unauthorizedPlants: response.unauthorizedPlants,
                    plant: action.plant,
                    index: action.index,
                  })
                ),
                catchError((error: HttpErrorResponse) =>
                  of(
                    loadCalculationHistoryFailure({
                      errorMessage: error.error.detail ?? error.message,
                      statusCode: error.status,
                      index: action.index,
                    })
                  )
                )
              )
          : of(
              loadCalculationHistoryFailure({
                errorMessage: 'User has no valid cost roles.',
                statusCode: undefined,
                index: action.index,
              })
            )
      )
    )
  );

  triggerDataLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadComparisonFeatureData),
      map(() => loadCalculations())
    )
  );
}
